/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { PlaylistClip } from "types/Clip";
import { FADE_OUT_CURVE } from "PhonosConstants";
import { millisecondsToSeconds } from "TimeHelper";
import { AudioEffect } from "types/AudioEffect";

export class FadeOutEffect extends AudioEffect {
  private readonly gainNode: GainNode;

  constructor(
    audioContext: AudioContext,
    clip: PlaylistClip,
    playlistTime: number,
    protected fadeDuration: number
  ) {
    super(audioContext, clip, playlistTime);
    this.gainNode = this.audioContext.createGain();

    const seekTimeRTC = Math.max(playlistTime - clip.startTime, 0);
    const fadeOutStartRTC = Math.max(clip.duration - fadeDuration, seekTimeRTC);
    const curveDuration = clip.duration - fadeOutStartRTC;
    const curvePercentage = curveDuration / fadeDuration;
    const appliedCurveLength = Math.max(
      curvePercentage * FADE_OUT_CURVE.length,
      2
    );
    const appliedCurve = FADE_OUT_CURVE.slice(
      FADE_OUT_CURVE.length - appliedCurveLength,
      FADE_OUT_CURVE.length
    );

    this.gainNode.gain.setValueCurveAtTime(
      appliedCurve,
      millisecondsToSeconds(clip.startTime + fadeOutStartRTC - playlistTime) +
        this.audioContext.currentTime,
      millisecondsToSeconds(curveDuration)
    );
  }

  hotswap(newClip: PlaylistClip): void {}

  get inputNode(): AudioNode {
    return this.gainNode;
  }

  get outputNode(): AudioNode {
    return this.gainNode;
  }

  disconnect(): void {
    this.gainNode.disconnect();
  }
}
